import React from 'react';
import {Link as RouterLink} from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    Box,
    Paper,
    Typography,
    Button,
    Stack,
    Divider,
    Alert,
    Tooltip,
    Link
} from '@mui/material';

/**
 * Themed error page:
 * - Uses MUI components and your palette/typography/shape settings.
 * - Matches HomePage spacing and Paper usage. :contentReference[oaicite:2]{index=2}
 * - Respects your theme colors (primary, error, background). :contentReference[oaicite:3]{index=3}
 */
export default function ErrorPage() {
    const [copied, setCopied] = React.useState(false);

    // Optional: capture minimal diagnostics from the location for easier support
    const diagnostics = React.useMemo(() => {
        const url = typeof window !== 'undefined' ? window.location.href : '';
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        return `url=${url}\nua=${ua}`;
    }, []);

    const copyDiagnostics = async () => {
        try {
            await navigator.clipboard.writeText(diagnostics);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    };

    return (
        <Box
            component="main"
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                p: 3
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: '100%',
                    maxWidth: 720,
                    p: {xs: 3, sm: 4},
                    bgcolor: 'background.paper',
                    borderRadius: 2
                }}
            >
                {/* Accent header strip in theme error color */}
                <Box
                    sx={{
                        height: 6,
                        width: '100%',
                        bgcolor: 'error.main',
                        borderRadius: 1,
                        mb: 2
                    }}
                />

                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                    <ErrorOutlineIcon fontSize="large" color="error"/>
                    <Typography variant="h4" component="h1">
                        Something went wrong
                    </Typography>
                </Stack>

                <Typography variant="body1" color="text.secondary" mb={3}>
                    An unexpected error occurred. If the problem persists, try reloading the page
                    or go back to the homepage.
                </Typography>

                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} mb={3}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<RefreshIcon/>}
                        onClick={() => window.location.reload()}
                    >
                        Reload page
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<HomeIcon/>}
                        component={RouterLink}
                        to="/"
                    >
                        Go to Home
                    </Button>
                    <Tooltip title={copied ? 'Copied!' : 'Copy diagnostics'}>
                        <Button
                            variant="text"
                            color="secondary"
                            startIcon={<ContentCopyIcon/>}
                            onClick={copyDiagnostics}
                        >
                            Copy info
                        </Button>
                    </Tooltip>
                </Stack>

                <Divider sx={{mb: 2}}/>

                <Alert severity="info" sx={{mb: 2}}>
                    Need help? Contact support and paste the diagnostics. You can also{' '}
                    <Link component={RouterLink} to="/" underline="hover" color="primary">
                        return to the homepage
                    </Link>
                    .
                </Alert>

                <Box
                    component="pre"
                    sx={{
                        bgcolor: 'background.default',
                        color: 'text.secondary',
                        p: 2,
                        borderRadius: 1,
                        overflowX: 'auto',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                        fontSize: 12,
                        whiteSpace: 'pre-wrap'
                    }}
                >
                    {diagnostics}
                </Box>
            </Paper>
        </Box>
    );
}